import { CodeIcon, ExternalLinkIcon } from "../../components/ui/Icons";
import { CodeBlock, Note } from "./shared";

interface MarketplacesDocsProps {
  marketplaceUrl: string;
}

export function MarketplacesDocs({ marketplaceUrl }: MarketplacesDocsProps) {
  return (
    <>
      {/* Overview */}
      <section id="overview" className="mb-16 scroll-mt-28">
        <h2 className="flex items-center gap-3 font-display text-2xl font-bold text-primary">
          <CodeIcon size={24} className="text-amber" />
          Overview
        </h2>
        <p className="mt-4 text-secondary">
          A plugin marketplace is a catalog that lets you distribute plugins to others. Marketplaces
          provide centralized discovery, version tracking, automatic updates, and support for
          multiple source types (git repositories, local paths, and more).
        </p>
        <p className="mt-4 text-secondary">Creating and distributing a marketplace involves:</p>
        <ol className="mt-4 space-y-2 text-secondary">
          <li>
            <strong>Creating plugins</strong>: Build one or more plugins with commands, agents,
            hooks, MCP servers, or LSP servers.
          </li>
          <li>
            <strong>Creating a marketplace file</strong>: Define a{" "}
            <code className="rounded bg-surface px-1.5 py-0.5 font-mono text-sm text-amber">
              marketplace.json
            </code>{" "}
            that lists your plugins and where to find them.
          </li>
          <li>
            <strong>Hosting the marketplace</strong>: Make it available via GitHub, a git server, or
            a direct URL.
          </li>
          <li>
            <strong>Sharing with users</strong>: Users add your marketplace and install plugins from
            it.
          </li>
        </ol>
      </section>

      {/* Create the Marketplace File */}
      <section id="marketplace-file" className="mb-16 scroll-mt-28">
        <h2 className="font-display text-2xl font-bold text-primary">
          Create the Marketplace File
        </h2>
        <p className="mt-4 text-secondary">
          Create{" "}
          <code className="rounded bg-surface px-1.5 py-0.5 font-mono text-sm text-amber">
            .claude-plugin/marketplace.json
          </code>{" "}
          in your repository root. This file defines your marketplace's name, owner information, and
          a list of plugins with their sources.
        </p>
        <p className="mt-4 text-secondary">
          Each plugin entry needs at minimum a{" "}
          <code className="rounded bg-surface px-1.5 py-0.5 font-mono text-sm text-amber">
            name
          </code>{" "}
          and
          <code className="rounded bg-surface px-1.5 py-0.5 font-mono text-sm text-amber">
            source
          </code>{" "}
          (where to fetch it from).
        </p>
        <div className="mt-6">
          <CodeBlock
            code={`{
  "name": "company-tools",
  "owner": {
    "name": "DevTools Team",
    "email": "devtools@example.com"
  },
  "plugins": [
    {
      "name": "code-formatter",
      "source": "./plugins/formatter",
      "description": "Format code with custom rules",
      "version": "1.0.0"
    },
    {
      "name": "test-runner",
      "source": "./plugins/test-runner",
      "description": "Run tests with detailed reports",
      "version": "2.1.0"
    }
  ]
}`}
          />
        </div>
        <div className="mt-6">
          <Note>
            <strong>Note:</strong> Plugins can't reference files outside their directory using paths
            like <code>../shared-utils</code>, because those files won't be copied. If you need to
            share files across plugins, use symlinks or restructure your marketplace so the shared
            directory is inside the plugin source path.
          </Note>
        </div>
      </section>

      {/* Marketplace Schema */}
      <section id="schema" className="mb-16 scroll-mt-28">
        <h2 className="font-display text-2xl font-bold text-primary">Marketplace Schema</h2>

        <h3 className="mt-8 text-lg font-semibold text-secondary">Root Fields</h3>
        <div className="mt-4 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-elevated">
                <th className="py-3 pr-4 text-left font-mono text-muted">Field</th>
                <th className="py-3 pr-4 text-left font-mono text-muted">Type</th>
                <th className="py-3 pr-4 text-left font-mono text-muted">Required</th>
                <th className="py-3 text-left font-mono text-muted">Description</th>
              </tr>
            </thead>
            <tbody className="text-secondary">
              <tr className="border-b border-surface">
                <td className="py-3 pr-4 font-mono text-amber">name</td>
                <td className="py-3 pr-4">string</td>
                <td className="py-3 pr-4">Yes</td>
                <td className="py-3">Unique marketplace identifier (kebab-case)</td>
              </tr>
              <tr className="border-b border-surface">
                <td className="py-3 pr-4 font-mono text-amber">owner</td>
                <td className="py-3 pr-4">object</td>
                <td className="py-3 pr-4">Yes</td>
                <td className="py-3">Marketplace maintainer information</td>
              </tr>
              <tr className="border-b border-surface">
                <td className="py-3 pr-4 font-mono text-amber">plugins</td>
                <td className="py-3 pr-4">array</td>
                <td className="py-3 pr-4">Yes</td>
                <td className="py-3">List of available plugins</td>
              </tr>
              <tr className="border-b border-surface">
                <td className="py-3 pr-4 font-mono text-amber">metadata</td>
                <td className="py-3 pr-4">object</td>
                <td className="py-3 pr-4">No</td>
                <td className="py-3">
                  Optional marketplace metadata (description, version, pluginRoot)
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <h3 className="mt-8 text-lg font-semibold text-secondary">Plugin Entry Fields</h3>
        <div className="mt-4 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-elevated">
                <th className="py-3 pr-4 text-left font-mono text-muted">Field</th>
                <th className="py-3 pr-4 text-left font-mono text-muted">Type</th>
                <th className="py-3 text-left font-mono text-muted">Description</th>
              </tr>
            </thead>
            <tbody className="text-secondary">
              <tr className="border-b border-surface">
                <td className="py-3 pr-4 font-mono text-amber">name</td>
                <td className="py-3 pr-4">string</td>
                <td className="py-3">Plugin identifier (kebab-case, required)</td>
              </tr>
              <tr className="border-b border-surface">
                <td className="py-3 pr-4 font-mono text-amber">source</td>
                <td className="py-3 pr-4">string | object</td>
                <td className="py-3">Where to find the plugin (required)</td>
              </tr>
              <tr className="border-b border-surface">
                <td className="py-3 pr-4 font-mono text-amber">description</td>
                <td className="py-3 pr-4">string</td>
                <td className="py-3">Brief plugin description</td>
              </tr>
              <tr className="border-b border-surface">
                <td className="py-3 pr-4 font-mono text-amber">version</td>
                <td className="py-3 pr-4">string</td>
                <td className="py-3">Plugin version</td>
              </tr>
              <tr className="border-b border-surface">
                <td className="py-3 pr-4 font-mono text-amber">author</td>
                <td className="py-3 pr-4">object</td>
                <td className="py-3">Plugin author information (name, email)</td>
              </tr>
              <tr className="border-b border-surface">
                <td className="py-3 pr-4 font-mono text-amber">repository</td>
                <td className="py-3 pr-4">string</td>
                <td className="py-3">Source code repository URL</td>
              </tr>
              <tr className="border-b border-surface">
                <td className="py-3 pr-4 font-mono text-amber">license</td>
                <td className="py-3 pr-4">string</td>
                <td className="py-3">SPDX license identifier (MIT, Apache-2.0, etc.)</td>
              </tr>
              <tr className="border-b border-surface">
                <td className="py-3 pr-4 font-mono text-amber">keywords</td>
                <td className="py-3 pr-4">array</td>
                <td className="py-3">Tags for plugin discovery</td>
              </tr>
              <tr className="border-b border-surface">
                <td className="py-3 pr-4 font-mono text-amber">category</td>
                <td className="py-3 pr-4">string</td>
                <td className="py-3">Plugin category for organization</td>
              </tr>
            </tbody>
          </table>
        </div>

        <div className="mt-6">
          <Note type="warning">
            <strong>Reserved names:</strong> The following marketplace names are reserved for
            official Anthropic use:
            <code>claude-code-marketplace</code>, <code>claude-code-plugins</code>,{" "}
            <code>claude-plugins-official</code>,<code>anthropic-marketplace</code>,{" "}
            <code>anthropic-plugins</code>, <code>agent-skills</code>, <code>life-sciences</code>.
          </Note>
        </div>
      </section>

      {/* Plugin Sources */}
      <section id="sources" className="mb-16 scroll-mt-28">
        <h2 className="font-display text-2xl font-bold text-primary">Plugin Sources</h2>

        <h3 className="mt-8 text-lg font-semibold text-secondary">Relative Paths</h3>
        <p className="mt-2 text-secondary">For plugins in the same repository:</p>
        <div className="mt-4">
          <CodeBlock
            code={`{
  "name": "my-plugin",
  "source": "./plugins/my-plugin"
}`}
          />
        </div>

        <h3 className="mt-8 text-lg font-semibold text-secondary">GitHub Repositories</h3>
        <div className="mt-4">
          <CodeBlock
            code={`{
  "name": "github-plugin",
  "source": {
    "source": "github",
    "repo": "owner/plugin-repo"
  }
}`}
          />
        </div>

        <h3 className="mt-8 text-lg font-semibold text-secondary">Git Repositories</h3>
        <div className="mt-4">
          <CodeBlock
            code={`{
  "name": "git-plugin",
  "source": {
    "source": "url",
    "url": "https://gitlab.com/team/plugin.git"
  }
}`}
          />
        </div>

        <h3 className="mt-8 text-lg font-semibold text-secondary">Advanced Plugin Entry</h3>
        <p className="mt-2 text-secondary">
          This example shows a plugin entry using many optional fields:
        </p>
        <div className="mt-4">
          <CodeBlock
            code={`{
  "name": "enterprise-tools",
  "source": {
    "source": "github",
    "repo": "company/enterprise-plugin"
  },
  "description": "Enterprise workflow automation tools",
  "version": "2.1.0",
  "author": {
    "name": "Enterprise Team",
    "email": "enterprise@example.com"
  },
  "repository": "https://github.com/company/enterprise-plugin",
  "license": "MIT",
  "keywords": ["enterprise", "workflow", "automation"],
  "category": "productivity",
  "commands": [
    "./commands/core/",
    "./commands/enterprise/"
  ],
  "agents": [
    "./agents/security-reviewer.md",
    "./agents/compliance-checker.md"
  ],
  "hooks": {
    "PostToolUse": [
      {
        "matcher": "Write|Edit",
        "hooks": [
          {
            "type": "command",
            "command": "\${CLAUDE_PLUGIN_ROOT}/scripts/validate.sh"
          }
        ]
      }
    ]
  }
}`}
          />
        </div>
        <p className="mt-4 text-sm text-muted">
          <strong>Note:</strong> Use{" "}
          <code className="rounded bg-surface px-1 py-0.5 font-mono text-xs">
            {"${CLAUDE_PLUGIN_ROOT}"}
          </code>{" "}
          in hooks and MCP server configs to reference files within the plugin's installation
          directory.
        </p>
      </section>

      {/* Host & Distribute */}
      <section id="hosting" className="mb-16 scroll-mt-28">
        <h2 className="font-display text-2xl font-bold text-primary">Host & Distribute</h2>

        <h3 className="mt-8 text-lg font-semibold text-secondary">Host on GitHub (Recommended)</h3>
        <p className="mt-2 text-secondary">GitHub provides the easiest distribution method:</p>
        <ol className="mt-4 space-y-2 text-secondary">
          <li>
            <strong>Create a repository</strong>: Set up a new repository for your marketplace
          </li>
          <li>
            <strong>Add marketplace file</strong>: Create{" "}
            <code className="rounded bg-surface px-1.5 py-0.5 font-mono text-sm">
              .claude-plugin/marketplace.json
            </code>{" "}
            with your plugin definitions
          </li>
          <li>
            <strong>Share with teams</strong>: Users add your marketplace with{" "}
            <code className="rounded bg-surface px-1.5 py-0.5 font-mono text-sm">
              claude plugin marketplace add owner/repo
            </code>
          </li>
        </ol>

        <h3 className="mt-8 text-lg font-semibold text-secondary">Host on Other Git Services</h3>
        <p className="mt-2 text-secondary">
          Any git hosting service works (GitLab, Bitbucket, self-hosted). Users add with the full
          repository URL:
        </p>
        <div className="mt-4">
          <CodeBlock
            language="shell"
            code="claude plugin marketplace add https://gitlab.com/company/plugins.git"
          />
        </div>

        <h3 className="mt-8 text-lg font-semibold text-secondary">Host via URL</h3>
        <p className="mt-2 text-secondary">
          You can also serve a marketplace.json directly via URL, like this marketplace:
        </p>
        <div className="mt-4">
          <CodeBlock language="shell" code={`claude plugin marketplace add ${marketplaceUrl}`} />
        </div>

        <h3 className="mt-8 text-lg font-semibold text-secondary">
          Test Locally Before Distribution
        </h3>
        <p className="mt-2 text-secondary">Test your marketplace locally before sharing:</p>
        <div className="mt-4">
          <CodeBlock
            language="shell"
            code={`claude plugin marketplace add ./my-local-marketplace
claude plugin install test-plugin@my-local-marketplace`}
          />
        </div>
      </section>

      {/* Troubleshooting */}
      <section id="troubleshooting" className="mb-16 scroll-mt-28">
        <h2 className="font-display text-2xl font-bold text-primary">Troubleshooting</h2>

        <h3 className="mt-8 text-lg font-semibold text-secondary">Marketplace Not Loading</h3>
        <p className="mt-2 text-secondary">
          <strong>Symptoms:</strong> Can't add marketplace or see plugins from it
        </p>
        <p className="mt-2 text-secondary">
          <strong>Solutions:</strong>
        </p>
        <ul className="mt-2 list-disc space-y-1 pl-6 text-secondary">
          <li>Verify the marketplace URL is accessible</li>
          <li>
            Check that{" "}
            <code className="rounded bg-surface px-1 py-0.5 font-mono text-xs">
              .claude-plugin/marketplace.json
            </code>{" "}
            exists at the specified path
          </li>
          <li>
            Ensure JSON syntax is valid using{" "}
            <code className="rounded bg-surface px-1 py-0.5 font-mono text-xs">
              claude plugin validate
            </code>
          </li>
          <li>For private repositories, confirm you have access permissions</li>
        </ul>

        <h3 className="mt-8 text-lg font-semibold text-secondary">Plugin Sources Not Resolving</h3>
        <p className="mt-2 text-secondary">
          <strong>Symptoms:</strong> Marketplace loads but can't install plugins
        </p>
        <p className="mt-2 text-secondary">
          <strong>Solutions:</strong>
        </p>
        <ul className="mt-2 list-disc space-y-1 pl-6 text-secondary">
          <li>Check that plugin directories contain required files</li>
          <li>For GitHub sources, ensure repositories are public or you have access</li>
          <li>Test plugin sources manually by cloning/downloading</li>
        </ul>

        <h3 className="mt-8 text-lg font-semibold text-secondary">
          Files Not Found After Installation
        </h3>
        <p className="mt-2 text-secondary">
          <strong>Symptoms:</strong> Plugin installs but references to files fail
        </p>
        <p className="mt-2 text-secondary">
          <strong>Cause:</strong> Plugins are copied to a cache directory. Paths that reference
          files outside the plugin's directory won't work.
        </p>
        <p className="mt-2 text-secondary">
          <strong>Solutions:</strong> Use symlinks or restructure so shared files are inside the
          plugin source path.
        </p>

        <div className="mt-8 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-elevated">
                <th className="py-3 pr-4 text-left font-mono text-muted">Error</th>
                <th className="py-3 pr-4 text-left font-mono text-muted">Cause</th>
                <th className="py-3 text-left font-mono text-muted">Solution</th>
              </tr>
            </thead>
            <tbody className="text-secondary">
              <tr className="border-b border-surface">
                <td className="py-3 pr-4 font-mono text-xs text-danger">
                  File not found: marketplace.json
                </td>
                <td className="py-3 pr-4">Missing manifest</td>
                <td className="py-3">Create .claude-plugin/marketplace.json</td>
              </tr>
              <tr className="border-b border-surface">
                <td className="py-3 pr-4 font-mono text-xs text-danger">Invalid JSON syntax</td>
                <td className="py-3 pr-4">Malformed JSON</td>
                <td className="py-3">Validate JSON with a linter</td>
              </tr>
              <tr className="border-b border-surface">
                <td className="py-3 pr-4 font-mono text-xs text-danger">
                  Invalid marketplace schema
                </td>
                <td className="py-3 pr-4">Missing required fields</td>
                <td className="py-3">Ensure name, owner, and plugins are present</td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      {/* See Also */}
      <section className="mb-16 rounded-xl border border-elevated bg-surface/30 p-6">
        <h2 className="font-display text-xl font-bold text-primary">See Also</h2>
        <ul className="mt-4 space-y-2">
          <li>
            <a
              href="https://docs.anthropic.com/en/docs/claude-code/plugins"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 text-amber hover:text-amber-hover"
            >
              <ExternalLinkIcon size={14} />
              Create Plugins - Official Claude Code Documentation
            </a>
          </li>
          <li>
            <a
              href="https://docs.anthropic.com/en/docs/claude-code/discover-plugins"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 text-amber hover:text-amber-hover"
            >
              <ExternalLinkIcon size={14} />
              Discover and Install Plugins
            </a>
          </li>
          <li>
            <a
              href="https://docs.anthropic.com/en/docs/claude-code/plugins-reference"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 text-amber hover:text-amber-hover"
            >
              <ExternalLinkIcon size={14} />
              Plugins Reference - Complete Technical Specifications
            </a>
          </li>
        </ul>
      </section>
    </>
  );
}
